import {
  AddressFormInputs,
  Button,
  Input,
  InputSelect,
  InputWrapper,
  type SelectItem,
} from '@vrplatform/components';
import {
  type gqlV2,
  showApiErrorNotification,
  useApiMutation,
  useDashboard,
  useInfiniteQuery,
  useMe,
  useQuery,
  useTeam,
  useTeamRole,
  useTeamSwitch,
  useTrackingEvent,
} from '@vrplatform/data';
import { AlertTriangleIcon } from '@vrplatform/icons';
import { Drawer, Logo } from '@vrplatform/components-legacy';
import {
  showErrorNotification,
  showSuccessNotification,
  useAppName,
} from '@vrplatform/theme';
import {
  parseAsBoolean,
  parseAsString,
  useQueryState,
} from '@vrplatform/query-params';
import {
  Avatar,
  Box,
  Center,
  Checkbox,
  Collapse,
  Divider,
  Group,
  ScrollArea,
  SegmentedControl,
  Stack,
  rem,
} from '@mantine/core';
import { Text } from '@mantine/core';
import { LoadingOverlay } from '@mantine/core';
import type { tenant_bool_exp } from '@vrplatform/graphql';
import { GL_HYPERLINE_PARTNER_PLAN_NAME } from '@vrplatform/hyperline-client';
import {
  VRP_TENANT_ID,
  formatAddress,
  getStateByIsoCode,
  validateTin,
  whereTenants,
} from '@vrplatform/ui-common';
import { useEffect, useMemo, useState } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useNavigate } from 'react-router';
import { HiddenFeatureIndicator } from '../../components';
import { DrawerCollapsableSection, DrawerHeader } from '../_components';
import { removeDrawerStateFromUrl, validateEmail } from '../../utils';

export const STORYBOOK_ADD_TEAM_PARTNER_BILLING_PLAN_QUERY_KEY =
  'add-team-partner-billing-plan';
export const STORYBOOK_ADD_TEAM_COPY_CHART_QUERY_KEY = 'add-team-copy-chart';
export const STORYBOOK_ADD_TEAM_COPY_CHART_VALUE_QUERY_KEY =
  'add-team-copy-chart-value';
export const STORYBOOK_ADD_TEAM_PARTNER_SELECT_QUERY_KEY =
  'add-team-partner-select';

export function useAddTeamDrawer() {
  const [opened, setOpened] = useQueryState('add-team-drawer', parseAsBoolean);
  const [teamName, setTeamName] = useQueryState('add-team-name', parseAsString);

  return {
    opened: !!opened,
    teamName,
    open: (teamName?: string) => {
      removeDrawerStateFromUrl();
      setTeamName(teamName || null);
      setOpened(true);
    },
    close: () => {
      setOpened(false);
      setTeamName(null);
    },
  };
}

function usePartnerBillingPlanQuery(partnerId: string) {
  return useQuery(
    (q, args) => {
      if (args.partnerId === VRP_TENANT_ID || !args.partnerId) {
        return {
          isVRTrustPartner: false,
        };
      }

      const team = q.tenantById({
        id: args.partnerId,
      });

      const isVRTrustPartner =
        team?.billingPlan === args.GL_HYPERLINE_PARTNER_PLAN_NAME;

      const isGl = team?.isGeneralLedger === true && team.id !== VRP_TENANT_ID;

      return {
        isVRTrustPartner: isVRTrustPartner || isGl,
      };
    },
    {
      skip: !partnerId,
      keepPreviousData: true,
      variables: {
        partnerId,
        GL_HYPERLINE_PARTNER_PLAN_NAME,
      },
      storybookQueryKey: STORYBOOK_ADD_TEAM_PARTNER_BILLING_PLAN_QUERY_KEY,
    }
  );
}

type FormInputs = {
  teamName: string;
  companyName: string;
  email: string;
  phone: string;
  taxId: string;
  address: Parameters<typeof formatAddress>[0];
  partnerId: string;
  type: 'propertyManager' | 'partner';
  enableGL: boolean;
  copyChartOfAccountFromTeamId: string | null;
};

export const AddTeamDrawer = () => {
  const { opened, close, teamName } = useAddTeamDrawer();
  const [taxDisabled, setTaxDisabled] = useState(false);
  const { isPartnerAdmin, isVrpAdmin } = useTeamRole();
  const navigate = useNavigate();
  const track = useTrackingEvent();
  const { id: meId } = useMe();
  const setTeamId = useTeamSwitch();
  const [{ partnerId: currentPartnerId, id: currentTeamId }] = useTeam();
  const [dashboard] = useDashboard();

  const methods = useForm<FormInputs>({
    values: {
      companyName: '',
      email: '',
      partnerId: isVrpAdmin
        ? VRP_TENANT_ID
        : dashboard === 'partner'
          ? currentTeamId
          : currentPartnerId,
      phone: '',
      enableGL: true,
      copyChartOfAccountFromTeamId: null,
      type: 'propertyManager',
      taxId: '',
      teamName: teamName || '',
      address: {
        city: '',
        countryCode: 'US',
        state: '',
        line1: '',
        line2: '',
        stateCode: 'AL',
        country: '',
        postcode: '',
      },
    },
  });

  const partnerId = useWatch({
    control: methods.control,
    name: 'partnerId',
  });

  const isEnableGL = useWatch({
    control: methods.control,
    name: 'enableGL',
  });

  const type = useWatch({
    control: methods.control,
    name: 'type',
  });

  const initialQuery = usePartnerBillingPlanQuery(partnerId);

  // reset enableGL to the partner billing plan
  useEffect(() => {
    if (initialQuery.data) {
      methods.setValue('enableGL', initialQuery.data.isVRTrustPartner);
    }
  }, [initialQuery.data, methods.setValue]);

  const { mutateAsync: apiAddTeam } = useApiMutation('post', '/teams');

  const submit = async (data: FormInputs) => {
    const hasAddress = !!data.address.line1?.trim();

    if (hasAddress && (!data.address.stateCode || !data.address.countryCode)) {
      return showErrorNotification({
        title: 'Validation error',
        message: 'Please select a country and state.',
        icon: <AlertTriangleIcon size={20} />,
      });
    }

    const state =
      (data.address.countryCode &&
        data.address.stateCode &&
        getStateByIsoCode(data.address.stateCode, data.address.countryCode)
          ?.name) ||
      '';

    const partnerId = data.type === 'partner' ? VRP_TENANT_ID : data.partnerId;

    await apiAddTeam({
      body: {
        name: data.teamName.trim(),
        type: data.type,
        partnerId,
        isOnboarding: data.type === 'partner' || data.enableGL,
        companyName: data.companyName.trim() || undefined,
        phone: data.phone.trim() || undefined,
        email: data.email.trim(),
        taxId: data.taxId.trim() || undefined,
        generalLedger:
          data.enableGL && data.type === 'propertyManager'
            ? {
                copyFromTeamId:
                  data.copyChartOfAccountFromTeamId || VRP_TENANT_ID,
                include: ['accounts', 'recurringFees', 'statementLayouts'],
              }
            : false,
        billingAddress: hasAddress
          ? {
              full: formatAddress(data.address),
              line1: data.address.line1.trim() || undefined,
              line2: data.address.line2?.trim() || undefined,
              city: data.address.city.trim() || undefined,
              countryCode: data.address.countryCode!,
              stateCode: data.address.stateCode!,
              postalCode: data.address.postcode.trim() || undefined,
              state,
            }
          : undefined,
        addMembers: [
          {
            userId: meId,
            role: 'admin',
          },
        ],
      },
      headers: {
        'x-team-id': partnerId,
      },
    })
      .then((res) => {
        showSuccessNotification({
          title: 'Team Created',
          message: 'Your team was successfully created.',
        });

        track('team_added', {
          tenantId: res.data.id,
          tenantName: data.teamName.trim(),
          partnerId: data.partnerId,
          userType: isPartnerAdmin ? 'partner' : 'user',
        });

        setTeamId(res.data.id);
        navigate('/');
        window.location.reload();
      })
      .catch((error) => {
        showApiErrorNotification({
          title: 'Team Creation Failed',
          error,
          defaultMessage: 'Failed to create team.',
        });
      });
  };

  return (
    <Drawer opened={opened} onClose={close} zIndex={300}>
      <DrawerHeader
        title="Add New Team"
        closeDrawer={close}
        containerSx={{
          paddingBottom: 0,
        }}
      />
      <Divider
        sx={(theme) => ({
          // marginInline: `-${theme.spacing.xs}`,
          borderTopColor: theme.colors.gray[2],
        })}
      />
      <Box
        component="form"
        onSubmit={methods.handleSubmit(submit)}
        onReset={() => {
          methods.reset();
          close();
        }}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflowY: 'hidden',
          overflowX: 'unset',
          position: 'relative',
        }}
      >
        <LoadingOverlay visible={initialQuery.isFetching} />

        <FormProvider {...methods}>
          <ScrollArea
            sx={{
              flex: 1,
            }}
          >
            <Stack mt="sm" gap="lg" pb="sm">
              <DrawerCollapsableSection title="General">
                <Stack gap="md">
                  <HiddenFeatureIndicator permission="vrp-admin" p="xs">
                    <Controller
                      name="type"
                      control={methods.control}
                      render={({ field, fieldState: { error } }) => {
                        const options = [
                          {
                            value: 'propertyManager',
                            label: 'Property Manager',
                          },
                          {
                            value: 'partner',
                            label: 'Partner',
                          },
                        ];

                        const value =
                          options.find((i) => i.value === field.value) || null;

                        return (
                          <InputWrapper
                            label="Team type"
                            required
                            error={error?.message}
                          >
                            <InputSelect
                              value={value}
                              type="single"
                              setValue={(value) =>
                                value?.value && field.onChange(value.value)
                              }
                              data={{
                                options,
                              }}
                              dropdownProps={{
                                withinPortal: true,
                                width: 'target',
                                zIndex: 400,
                              }}
                              inputProps={{
                                placeholder: 'Select team type',
                                error: !!error?.message,
                              }}
                            />
                          </InputWrapper>
                        );
                      }}
                    />
                  </HiddenFeatureIndicator>

                  <PartnerSelect />

                  <Controller
                    name="teamName"
                    defaultValue=""
                    control={methods.control}
                    rules={{
                      required: 'Team name is required',
                      validate: (value) =>
                        !!value.trim() || 'Team name is required',
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <InputWrapper
                        label="Team name"
                        required
                        error={error?.message}
                      >
                        <Input
                          {...field}
                          placeholder="Team name"
                          error={error?.message}
                        />
                      </InputWrapper>
                    )}
                  />
                </Stack>
              </DrawerCollapsableSection>

              {type !== 'partner' && (
                <DrawerCollapsableSection
                  title={
                    !initialQuery.data?.isVRTrustPartner
                      ? 'Product'
                      : 'General ledger'
                  }
                >
                  <Box px={4}>
                    {!initialQuery.data?.isVRTrustPartner && (
                      <Controller
                        control={methods.control}
                        name="enableGL"
                        defaultValue={true}
                        render={({ field, fieldState }) => (
                          <InputWrapper error={fieldState.error?.message}>
                            <SegmentedControl
                              size="xs"
                              data={[
                                {
                                  label: (
                                    <Group gap={10} justify="center">
                                      <Center
                                        sx={(theme) => ({
                                          borderRadius: '100%',
                                          backgroundColor: theme.white,
                                          overflow: 'hidden',
                                          width: 18,
                                          height: 18,
                                        })}
                                      >
                                        <Logo width={16} />
                                      </Center>
                                      <Text fw={500} size="xs">
                                        VRTrust
                                      </Text>
                                    </Group>
                                  ),
                                  value: 'vrtrust',
                                },
                                {
                                  label: (
                                    <Group gap={10} justify="center">
                                      <Center
                                        sx={(theme) => ({
                                          borderRadius: '100%',
                                          backgroundColor: theme.white,
                                          overflow: 'hidden',
                                          width: 18,
                                          height: 18,
                                        })}
                                      >
                                        <Avatar
                                          src={
                                            'https://imagedelivery.net/Ftdv9WftbW1DctaiLSVfFA/134dcf3d-e3d3-4ff1-5668-c38ab408ab00/smol2'
                                          }
                                          size={rem(18)}
                                          sx={(theme) => ({
                                            border: `1px solid ${theme.colors.gray[2]}`,
                                          })}
                                        />
                                      </Center>
                                      <Text fw={500} size="xs">
                                        VRIntegrations
                                      </Text>
                                    </Group>
                                  ),
                                  value: 'vrintegrations',
                                },
                              ]}
                              value={field.value ? 'vrtrust' : 'vrintegrations'}
                              fullWidth
                              onChange={(value) => {
                                if (value === 'vrtrust') {
                                  field.onChange(true);
                                } else {
                                  field.onChange(false);
                                  methods.setValue(
                                    'copyChartOfAccountFromTeamId',
                                    null
                                  );
                                }
                                methods.clearErrors('email');
                              }}
                            />
                          </InputWrapper>
                        )}
                      />
                    )}
                    <Collapse in={isEnableGL && type === 'propertyManager'}>
                      <ChartOfAccountCopySelect />
                    </Collapse>
                  </Box>
                </DrawerCollapsableSection>
              )}

              <DrawerCollapsableSection title="Statements">
                <Stack gap="sm">
                  <Controller
                    name="companyName"
                    defaultValue=""
                    control={methods.control}
                    render={({ field, fieldState: { error } }) => (
                      <InputWrapper label="Company name" error={error?.message}>
                        <Input
                          {...field}
                          placeholder="Company name"
                          error={error?.message}
                        />
                      </InputWrapper>
                    )}
                  />
                  <Controller
                    name="email"
                    defaultValue=""
                    control={methods.control}
                    rules={{
                      validate: (value) => validateEmail(value),
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <InputWrapper label="Email" error={error?.message}>
                        <Input
                          {...field}
                          placeholder="Email"
                          type="email"
                          error={error?.message}
                        />
                      </InputWrapper>
                    )}
                  />
                  <Controller
                    name="phone"
                    defaultValue=""
                    control={methods.control}
                    render={({ field, fieldState: { error } }) => (
                      <InputWrapper label="Phone" error={error?.message}>
                        <Input
                          {...field}
                          placeholder="Phone"
                          error={error?.message}
                        />
                      </InputWrapper>
                    )}
                  />

                  <Box>
                    <Controller
                      control={methods.control}
                      name="taxId"
                      defaultValue=""
                      rules={{
                        required: taxDisabled
                          ? undefined
                          : 'Tax Identification Number is required',
                        validate: taxDisabled
                          ? undefined
                          : (v) => validateTin(v),
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <InputWrapper
                          label="TIN (US only)"
                          error={error?.message}
                          mb="sm"
                          required={!taxDisabled}
                        >
                          <Input
                            {...field}
                            placeholder="Tax Identification Number"
                            autoComplete="off"
                            error={!!error}
                            disabled={taxDisabled}
                          />
                        </InputWrapper>
                      )}
                    />

                    <Checkbox
                      label="I do not have a TIN"
                      size="xs"
                      mb="sm"
                      checked={taxDisabled}
                      onChange={() => {
                        setTaxDisabled((e) => !e);
                        methods.clearErrors('taxId');
                        methods.setValue('taxId', '');
                      }}
                    />
                  </Box>
                </Stack>
              </DrawerCollapsableSection>

              <DrawerCollapsableSection title="Address">
                <Box pb="sm">
                  <AddressFormInputs disableType="switch" />
                </Box>
              </DrawerCollapsableSection>
            </Stack>
          </ScrollArea>
          <Box mb="sm">
            <Divider mb={'sm'} />
            <Group justify="right">
              <Button type="reset" disabled={methods.formState.isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={methods.formState.isSubmitting}
                sx={{
                  flex: 1,
                }}
              >
                Create team
              </Button>
            </Group>
          </Box>
        </FormProvider>
      </Box>
    </Drawer>
  );
};

const ChartOfAccountCopySelect = () => {
  const methods = useFormContext<FormInputs>();
  const selectedPartnerId = methods.watch('partnerId');
  const [search, setSearch] = useState('');
  const [{ partnerId }] = useTeam();

  const { appName } = useAppName();

  const [{ id: teamId }] = useTeam();

  const queryData = useInfiniteQuery(
    (q, args) => {
      const where: tenant_bool_exp = {
        ...whereTenants({
          search: args.search,
          dashboard: 'propertyManager',
          partnerId: args.partnerId,
          product: 'vrtrust',
        }),
        id: args.teamId ? { _neq: args.teamId } : undefined,
      };

      const list = q
        .tenant({
          where,
          order_by: [{ name: 'asc_nulls_last' }],
        })
        .map((tenant) => ({
          value: tenant.id,
          label: tenant.name || '',
        }));

      const aggregate =
        q
          .tenantAggregate({
            where,
          })
          .aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      variables: {
        teamId,
        search: search.trim(),
        partnerId: selectedPartnerId || partnerId,
      },
      storybookQueryKey: STORYBOOK_ADD_TEAM_COPY_CHART_QUERY_KEY,
    }
  );

  return (
    <Controller
      control={methods.control}
      name="copyChartOfAccountFromTeamId"
      render={({ field }) => {
        const { error, isLoading, data } = useQuery(
          (q, args) => {
            if (!args.id)
              return {
                value: null,
              };

            return {
              value:
                q
                  .tenant({
                    where: {
                      id: { _eq: args.id },
                    },
                  })
                  .map((tenant) => ({
                    value: tenant.id,
                    label: tenant.name || '',
                  }))[0] || undefined,
            };
          },
          {
            skip: !field.value,
            variables: {
              id: field.value,
            },
            keepPreviousData: true,
            storybookQueryKey: STORYBOOK_ADD_TEAM_COPY_CHART_VALUE_QUERY_KEY,
          }
        );

        const defaultLabel = `${appName} - Default chart of accounts`;
        const defaultValue = { value: null, label: defaultLabel };
        const value = field.value ? data || defaultValue : defaultValue;

        return (
          <InputWrapper
            label="Copy chart of accounts from"
            description="Team options are filtered down to the currently selected partner"
            mt="sm"
            inputWrapperOrder={['label', 'input', 'description', 'error']}
          >
            <InputSelect
              type="single"
              value={value.value?.value ? value.value : null}
              infiniteData={{ ...queryData, setSearch }}
              setValue={(v) => field.onChange(v?.value || null)}
              inputProps={{
                loadingQuery: isLoading,
                error: error?.message,
                placeholder: defaultLabel,
                withClearButton: true,
              }}
              dropdownProps={{
                withinPortal: true,
                position: 'top',
                zIndex: 2000,
                width: 'target',
              }}
            />
          </InputWrapper>
        );
      }}
    />
  );
};

const PartnerSelect = () => {
  const methods = useFormContext<FormInputs>();
  const teamType = methods.watch('type');

  const queryData = useQuery(
    (q) => {
      const where: gqlV2.tenant_bool_exp = {
        type: { _eq: 'partner' },
        id: { _neq: VRP_TENANT_ID },
      };

      const hasAccessToMultiplePartnes =
        q
          .tenant({
            where: { type: { _eq: 'partner' } },
            limit: 2,
          })
          .map((x) => x.id).length > 1;

      const list = q
        .tenant({
          order_by: [{ type: 'asc_nulls_last' }, { name: 'asc' }],
          where,
        })
        .map<SelectItem>((i) => ({
          value: i.id,
          label: i.name || '',
        }));

      return {
        list,
        hasAccessToMultiplePartnes,
      };
    },
    {
      keepPreviousData: true,
      queryKey: ['partner-select'],
      storybookQueryKey: STORYBOOK_ADD_TEAM_PARTNER_SELECT_QUERY_KEY,
    }
  );

  if (!queryData.data?.hasAccessToMultiplePartnes) return null;

  return (
    <Collapse in={teamType === 'propertyManager'}>
      <Controller
        control={methods.control}
        name="partnerId"
        render={({ field }) => {
          const options = queryData.data?.list || [];

          const vrpOption = {
            label: 'VRPlatform',
            value: VRP_TENANT_ID,
          };

          const value =
            [vrpOption, ...options].find((i) => i.value === field.value) ||
            null;

          const customActionTop = useMemo(
            () => ({
              label: 'VRPlatform',
              icon: (
                <Center
                  sx={{
                    borderRadius: '100%',
                    overflow: 'hidden',
                  }}
                >
                  <Logo width={16} />
                </Center>
              ),
              onSubmit: () => field.onChange(VRP_TENANT_ID),
            }),
            [field.onChange]
          );

          return (
            <InputWrapper label="Partner" required>
              <InputSelect
                data={{
                  options,
                  error: queryData.error,
                  loading: queryData.isLoading,
                }}
                type="single"
                value={value}
                setValue={(value) => {
                  if (!value?.value) return;
                  field.onChange(value.value);
                }}
                inputProps={{
                  placeholder: 'Partner team',
                }}
                dropdownProps={{
                  withinPortal: true,
                  width: 'target',
                  zIndex: 400,
                }}
                customActionTop={customActionTop}
              />
            </InputWrapper>
          );
        }}
      />
    </Collapse>
  );
};
